import { CONFIG } from 'src/environments/environment';
import { useSystemStore } from 'src/store/SystemStore';
import { useDeviceSyncStore } from 'src/store/DeviceSync';
import { isUuid, useDeviceContextStore, type DeviceContextFields } from 'src/store/DeviceContext';

import { buildDeviceIdentityHeaders, encodeDeviceContextHeaders } from './device-metadata-headers';

/** Includes names, values, separators, and both compatibility copies of the JSON. */
export const HEALTH_METADATA_MAX_HEADER_BYTES = 4_096;
const MAX_FIELD_LENGTH = 256;
const UUID_FIELDS = ['businessId', 'businessDeviceId', 'businessLocationId', 'terminalId'] as const;
const TEXT_FIELDS = [
  'accountNumber',
  'deviceFingerprint',
  'publicKeyFingerprint',
  'deviceName',
  'deviceType',
  'environment',
  'lastResolvedAt',
] as const;
const NULLABLE_FIELDS = [
  'businessLocationId',
  'terminalId',
  'deviceFingerprint',
  'deviceName',
] as const;

/** A short, printable string or undefined. Shared with `src/features/pos-session-file`. */
export function boundedString(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim() || value.length > MAX_FIELD_LENGTH)
    return undefined;
  // Reject control characters before values reach Fetch, XHR, or server logs.
  if (Array.from(value).some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127))
    return undefined;
  return value;
}

/** `dev`/`local`/`development` → `dev`, `prod`/`production` → `production`. */
export function environmentKey(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (['dev', 'development', 'local'].includes(normalized)) return 'dev';
  if (['prod', 'production'].includes(normalized)) return 'production';
  return normalized;
}

function isHealthDestination(url: string, baseAthUrl: string, baseUrl: string): boolean {
  const requested = new URL(url);
  if (!['http:', 'https:'].includes(requested.protocol) || requested.username || requested.password)
    return false;
  return [
    [baseAthUrl, '/status?type=server'],
    [baseUrl, '/api/health'],
  ].some(([base, path]) => {
    if (!base?.trim()) return false;
    // Append to the configured path: URL('/api/health', base) would lose /b.
    try {
      return requested.href === new URL(`${base.trim().replace(/\/$/, '')}${path}`).href;
    } catch {
      // An invalid ATH override must not discard metadata for a valid data host.
      return false;
    }
  });
}

function snapshotContext(snapshot: DeviceContextFields): Partial<DeviceContextFields> {
  const context: Partial<DeviceContextFields> = {};
  UUID_FIELDS.forEach((key) => {
    if (isUuid(snapshot[key])) context[key] = snapshot[key];
  });
  TEXT_FIELDS.forEach((key) => {
    const value = boundedString(snapshot[key]);
    if (key === 'lastResolvedAt' && value && !Number.isFinite(Date.parse(value))) return;
    if (value !== undefined) context[key] = value;
  });
  NULLABLE_FIELDS.forEach((key) => {
    if (snapshot[key] === null) context[key] = null;
  });
  return context;
}

function headerBytes(headers: Record<string, string>): number {
  // Every value has been encoded/filtered to ASCII, so code units equal bytes.
  return Object.entries(headers).reduce(
    (total, [key, value]) => total + key.length + value.length + 4,
    0
  );
}

/**
 * Observational metadata only. Never resolves a device, reads secure storage,
 * obtains a token, mutates stores, or waits for hydration. Persisted fields are
 * useful even while status is idle; conflicting scopes are not useful evidence.
 * A metadata failure must never turn a healthy service into an outage.
 */
export function getHealthRequestHeaders(url: string): Record<string, string> {
  try {
    const system = useSystemStore.getState();
    if (!isHealthDestination(url, system.baseAthUrl, system.baseUrl)) return {};

    const snapshot = useDeviceContextStore.getState();
    const sync = useDeviceSyncStore.getState();
    const knownTenants = [snapshot.businessId, sync.tenantId, sync.businessData?.id].filter(
      Boolean
    );
    if (new Set(knownTenants).size > 1) return {};
    if (
      snapshot.environment &&
      environmentKey(snapshot.environment) !== environmentKey(CONFIG.environment)
    )
      return {};

    const context = snapshotContext(snapshot);
    const hasData = Object.entries(context).some(
      ([key, value]) => value != null && key !== 'environment' && key !== 'lastResolvedAt'
    );
    if (!hasData) return {};

    // Enrich only a matching device tenant; never infer ownership from another cache.
    const businessName =
      context.businessId === sync.businessData?.id
        ? boundedString(sync.businessData?.name)
        : undefined;
    const identityHeaders = buildDeviceIdentityHeaders(
      context,
      boundedString(system.currentDeviceType),
      businessName
    );
    const headers: Record<string, string> = {};
    Object.entries(identityHeaders).forEach(([key, value]) => {
      if (typeof value === 'string' && value.length > 0 && /^[\x20-\x7e]+$/.test(value))
        headers[key] = value;
    });
    if (headerBytes(headers) > HEALTH_METADATA_MAX_HEADER_BYTES) return {};

    const withContext = { ...headers, ...encodeDeviceContextHeaders(context) };
    // Large names/contexts must not cause a proxy's header-size rejection. Keep
    // the small identity headers if both encoded copies exceed our budget.
    return headerBytes(withContext) <= HEALTH_METADATA_MAX_HEADER_BYTES ? withContext : headers;
  } catch {
    return {};
  }
}
